import React, {useEffect, useMemo, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import {useTranslation} from "react-i18next";
import {SalesPerson, DbCustomer} from "sharedlibs";
import SalesPersonService from "../../services/SalesPersonService";
import CustomerService from "../../services/CustomerService";

export interface CustomerSalespersonSectionProps {
  customer: DbCustomer;
  onChanged: (updated: DbCustomer) => void;
  disabled?: boolean;
}

/**
 * Shows the customer's currently-assigned salesperson and lets an admin
 * change or clear the assignment inline. The single source of truth is
 * Customer.salespersonId; this section talks to the dedicated assignment
 * endpoint rather than the generic customer-update form.
 */
export default function CustomerSalespersonSection({
  customer,
  onChanged,
  disabled = false,
}: Readonly<CustomerSalespersonSectionProps>) {
  const {t} = useTranslation();

  const [salesPersons, setSalesPersons] = useState<SalesPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(customer.salespersonId ?? null);

  useEffect(() => {
    setLoading(true);
    SalesPersonService.getAllSalesPersons()
      .then((list) => setSalesPersons(list))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setDraftId(customer.salespersonId ?? null);
  }, [customer.salespersonId]);

  const currentSalesperson = useMemo(
    () => salesPersons.find((s) => s.id === customer.salespersonId) ?? null,
    [salesPersons, customer.salespersonId],
  );

  const draftSalesperson = useMemo(
    () => salesPersons.find((s) => s.id === draftId) ?? null,
    [salesPersons, draftId],
  );

  async function persist(nextId: string | null) {
    if (!customer.id) return;
    setSubmitting(true);
    setError(null);
    try {
      // The server writes the business audit entry for this change.
      const updated = await CustomerService.assignSalesperson(customer.id, nextId);
      onChanged(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id="customer-salesperson-panel">
        <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
          <Typography>{t("salesperson.responsibleSalesperson")}</Typography>
          {currentSalesperson ? (
            <Chip
              size="small"
              icon={<PersonIcon fontSize="small" />}
              label={currentSalesperson.lastName}
              color="primary"
              variant="outlined"
            />
          ) : (
            <Chip
              size="small"
              icon={<PersonOffIcon fontSize="small" />}
              label={t("salesperson.unassigned")}
              variant="outlined"
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {loading ? (
          <CircularProgress size={24} />
        ) : !editing ? (
          <Stack direction="row" spacing={2} sx={{alignItems: 'center', flexWrap: 'wrap'}}>
            <Typography variant="body1">
              {currentSalesperson
                ? currentSalesperson.lastName
                : t("salesperson.noSalespersonAssigned")}
            </Typography>
            <Box sx={{flex: 1}} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setEditing(true)}
              disabled={disabled || submitting}
            >
              {currentSalesperson ? t("salesperson.change") : t("salesperson.assign")}
            </Button>
            {currentSalesperson && (
              <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={() => persist(null)}
                disabled={disabled || submitting}
              >
                {t("salesperson.clear")}
              </Button>
            )}
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} sx={{alignItems: 'center', flexWrap: 'wrap'}}>
            <Autocomplete
              sx={{minWidth: 320}}
              size="small"
              options={salesPersons.filter((s) => !s.disabled || s.id === customer.salespersonId)}
              value={draftSalesperson}
              getOptionLabel={(s) => s.lastName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => setDraftId(value?.id ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("salesperson.pickSalesperson")}
                  variant="outlined"
                />
              )}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => persist(draftId)}
              disabled={
                disabled ||
                submitting ||
                draftId === (customer.salespersonId ?? null)
              }
            >
              {t("activities.save")}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setDraftId(customer.salespersonId ?? null);
                setEditing(false);
                setError(null);
              }}
              disabled={submitting}
            >
              {t("activities.cancel")}
            </Button>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
